import { createPortal } from "react-dom";
import { useEffect, useState } from "react";
import { LegalLayout } from "../../components/public/legal-layout";

const DOCS_CHAPTERS = [
  {
    id: "introduction",
    sidebarTitle: "Why UGC Persona exists",
    sidebarDescription: "What the product is for and why the wizard exists.",
    eyebrow: "Introduction",
    title: "Introduction: Why UGC Persona exists",
    description:
      "UGC Persona was created to help brands tell their story through realistic AI people who speak with your message, your positioning, and your ideas. Instead of piecing together multiple tools, you move through one guided wizard that starts before Step 1 and turns a visual concept into a script, then into a voiceover, and finally into a finished video.",
    flow:
      "Wizard start -> Image generation -> Copy generation -> Voice generation -> Video generation -> Social publishing",
    topics: [
      {
        title: "A guided workflow, not a pile of tools",
        body: "The product is built as a wizard with clear strategic touchpoints: image, text, voiceover, and final video."
      },
      {
        title: "Your brand idea stays at the center",
        body: "The purpose of the system is simple: help you express your brand through realistic people, social-style storytelling, and controlled creative direction."
      },
      {
        title: "Best-in-class models, already prepared",
        body: "Our job is to offer a system that is already set up with excellent AI models, grouped and ready to use, so you can focus on the creative result instead of the technical stack."
      }
    ]
  },
  {
    id: "wizard-start",
    sidebarTitle: "Wizard start",
    sidebarDescription: "Choose the image path and whether edit is included.",
    eyebrow: "Wizard",
    title: "Wizard: choose the image path before Step 1",
    description:
      "This is the setup screen that comes before Step 1. The wizard asks two key questions: whether you want to generate the influencer image or upload one, and whether the workflow should include the image edit step.",
    topics: [
      {
        title: "Generate or upload",
        body: "If you already have an image for the person, you can upload it. If not, the wizard can generate a new one from scratch."
      },
      {
        title: "Include or skip edit",
        body: "The second choice decides whether the flow continues into image editing or skips directly toward copy generation."
      },
      {
        title: "Why it matters",
        body: "This screen defines the rest of the session, because it determines which steps will appear and how the flow should continue."
      }
    ],
    screenshot: {
      code: "WIZ-00",
      title: "Wizard start",
      caption: "The initial wizard screen asks whether to generate or upload the influencer image and whether the image edit step should be included before Step 1.",
      filename: "docs-wizard-start.png",
      src: "/media/docs/wizard.png",
      alt: "UGC Persona wizard start screenshot"
    }
  },
  {
    id: "image-generation",
    sidebarTitle: "Start with the image",
    sidebarDescription: "Choose whether to upload a person or generate one.",
    eyebrow: "Step 1",
    title: "Step 1: Start with the image",
    description:
      "The wizard begins with a key question: do you want to upload a person you already have, or generate one from scratch? This first decision is important because it determines whether the workflow starts from your own source image or from a newly created AI persona.",
    topics: [
      {
        title: "Upload an existing person",
        body: "You can upload a photo you already prepared, including a real photo of yourself or a previously generated image of a person you want to use as the base avatar."
      },
      {
        title: "Generate a new AI person",
        body: "If you do not already have a base image, the wizard can generate one for you and move it into the rest of the pipeline."
      },
      {
        title: "Why this question matters",
        body: "Based on this answer, the wizard decides whether to show the upload flow or the generation flow first, and then whether to offer the next editing step for the image you uploaded or generated."
      }
    ],
    screenshot: {
      code: "DOC-01",
      title: "Image generation",
      caption: "The image generation screen lets you choose the aspect ratio, write the prompt, browse examples, and generate the base influencer image.",
      filename: "docs-01-wizard-start.png",
      src: "/media/docs/step1.png",
      alt: "UGC Persona Step 1 screenshot"
    }
  },
  {
    id: "image-editing",
    sidebarTitle: "Image edit",
    sidebarDescription: "Refine the base image with a prompt or extra reference.",
    eyebrow: "Step 2",
    title: "Step 2: Image Edit",
    description:
      "This is the optional refinement stage between image generation and copy generation. You start from the base image created in Step 1, optionally add a reference image, write an edit prompt, and then either generate the edited version or skip directly to copy.",
    topics: [
      {
        title: "Base image from Step 1",
        body: "The edit flow starts from the image already created or uploaded in Step 1, which stays visible as the base asset for the revision."
      },
      {
        title: "Optional reference upload",
        body: "If you want more control, you can upload an additional image for the prompt and use it as a reference for the edit."
      },
      {
        title: "Generate or skip",
        body: "The main action generates the edited image in the preview panel on the right. If the base image is already good enough, you can skip the edit and continue straight to copy."
      }
    ],
    screenshot: {
      code: "DOC-02",
      title: "Image edit",
      caption: "The image edit screen starts from the Step 1 base image, allows an optional reference upload and edit prompt, and shows the revised result in the preview panel.",
      filename: "docs-02-image-edit.png",
      src: "/media/docs/step2.png",
      alt: "UGC Persona Step 2 image edit screenshot"
    }
  },
  {
    id: "copy-generation",
    sidebarTitle: "Generate the copy",
    sidebarDescription: "Turn a URL or notes into a social-ready script.",
    eyebrow: "Step 3",
    title: "Step 3: Generate the copy",
    description:
      "Copy Generation is where the system transforms your product page, your notes, and your platform choice into a structured social script. The output is designed to match the channel, the goal, and the type of content you want to create.",
    topics: [
      {
        title: "Platform, content type, language, and duration",
        body: "You choose the platform first, because the tone changes based on where the video will be used. You also choose whether the content is organic or ads, the language, and the target script length in seconds. For longer explanations or story-led messaging, 20-30 seconds can be a strong range."
      },
      {
        title: "Ads output is already structured",
        body: "When you choose ads, the system can prepare the supporting assets you need, including headline, description, and primary text, already aligned to the expected character constraints."
      },
      {
        title: "URL fetch and editable social script",
        body: "If you provide a product or website URL, the system fetches the page content for you, extracts the relevant information, and turns it into a social-style script with a hook, story development, and final CTA. On the right side you can review and edit the result, while the script review area shows character count and estimated duration so you always know how the text is performing."
      }
    ],
    resources: [
      {
        label: "Meta video ads guide",
        href: "https://www.facebook.com/business/ads/video-ad-format"
      },
      {
        label: "Meta Reels ads guide",
        href: "https://www.facebook.com/help/instagram/546362593027755"
      },
      {
        label: "TikTok ads best practices",
        href: "https://ads.us.tiktok.com/help/article/tiktok-ads-best-practices?lang=en"
      }
    ],
    screenshot: {
      code: "DOC-03",
      title: "Copy generation",
      caption: "The copy generation screen combines platform, content type, language, and duration inputs with the generated script, character counts, and ad-ready copy fields.",
      filename: "docs-03-copy-generation.png",
      src: "/media/docs/step3.png",
      alt: "UGC Persona Step 3 screenshot"
    }
  },
  {
    id: "voice-generation",
    sidebarTitle: "Generate the voiceover",
    sidebarDescription: "Convert the approved script into spoken audio.",
    eyebrow: "Step 4",
    title: "Step 4: Generate the voiceover",
    description:
      "Text to speech is where the script becomes a realistic voiceover. The system gives you access to preselected high-quality voices that are designed to sound professional, natural, and suitable for multilingual brand communication.",
    topics: [
      {
        title: "Professional preset voices",
        body: "You can choose from realistic male and female voices, already prepared inside the system, with language support that follows the language you selected in the previous step."
      },
      {
        title: "Enhance text before voice generation",
        body: "Before generating the voiceover, the system can refine the script with performance-friendly tags and reading cues to create a more realistic spoken result."
      },
      {
        title: "Follow the important gradient-border actions",
        body: "Throughout the wizard, the key actions are always highlighted by the buttons with the gradient border. Those are the actions that move you step by step through the flow. This chapter ends when you generate the voice and listen to the final speaker track."
      }
    ],
    screenshot: {
      code: "DOC-04",
      title: "Voice generation",
      caption: "The voice generation screen shows the selected voice, the editable script, the generated waveform, and the confirm audio action that moves the flow forward.",
      filename: "docs-04-voice-generation.png",
      src: "/media/docs/step4.png",
      alt: "UGC Persona Step 4 screenshot"
    }
  },
  {
    id: "video-generation",
    sidebarTitle: "Generate the video",
    sidebarDescription: "Combine image and audio into the final output.",
    eyebrow: "Step 5",
    title: "Step 5: Generate the video",
    description:
      "Video generation is the most important production step. Here the approved image and the approved audio are merged into a final video that is ready for short-form social content.",
    topics: [
      {
        title: "Recommended format",
        body: "We recommend generating in 9:16 and, for now, 720p. This is the safest and most practical starting point for social-first UGC-style content."
      },
      {
        title: "The wizard remembers your approved image",
        body: "The video step uses the image you created in Step 1, or the edited version from Step 2 if you changed it, so the visual identity stays consistent."
      },
      {
        title: "Review in Reels and Stories",
        body: "After you click generate, you can review the final output inside both Reels and Stories previews to understand how it will look once published."
      }
    ],
    screenshot: {
      code: "DOC-05",
      title: "Video generation",
      caption: "The video generation screen combines the image and audio inputs with the 9:16 preview, resolution selector, and the download or publish actions.",
      filename: "docs-05-video-preview.png",
      src: "/media/docs/step5.png",
      alt: "UGC Persona Step 5 screenshot"
    }
  },
  {
    id: "publishing",
    sidebarTitle: "Publish to social",
    sidebarDescription: "Send the final video to Instagram, Facebook, or TikTok.",
    eyebrow: "Step 6",
    title: "Step 6: Publish to social",
    description:
      "The publishing area is still under construction, but the current behavior is already important to understand before you connect your channels.",
    topics: [
      {
        title: "TikTok publishes through drafts",
        body: "On TikTok, publication currently happens through draft delivery. The user receives the draft flow and completes the final publication from TikTok."
      },
      {
        title: "Instagram and Facebook are direct",
        body: "On Instagram and Facebook, the goal is direct publication and posting from the connected destination."
      },
      {
        title: "Current status",
        body: "This chapter should be presented as an evolving area of the platform, with TikTok draft logic already clear and the broader social publishing flow still being completed."
      }
    ],
    screenshot: {
      code: "DOC-06",
      title: "Publish destinations",
      caption: "The publish screen shows the destination cards, caption input, and delivery status panel for Instagram, Facebook, and TikTok.",
      filename: "docs-06-publish-destinations.png",
      src: "/media/docs/step6.png",
      alt: "UGC Persona Step 6 screenshot"
    }
  },
  {
    id: "connections",
    sidebarTitle: "Connections",
    sidebarDescription: "Link one account per platform and pick a destination.",
    eyebrow: "Connections",
    title: "Connections: Connect one account per platform",
    description:
      "The Connections area is simple by design. You connect one account per platform, then choose the destination that should receive the publication.",
    topics: [
      {
        title: "One account per platform",
        body: "You can connect one Meta account and one TikTok account for the current workspace. If you need a different client setup, the cleanest approach is to use a separate account for that client."
      },
      {
        title: "Multiple pages can still appear",
        body: "If the connected account manages multiple pages or destinations, you will see them and can choose which one should be selected."
      },
      {
        title: "Selected destination matters",
        body: "After the connection is completed, the interface shows the selected destination on the right side, such as Instagram or TikTok, so you know where the publishing flow is pointing."
      }
    ],
    screenshot: {
      code: "DOC-07",
      title: "Connections overview",
      caption: "The connections screen shows the Meta and TikTok account panels, the selected Instagram destination, and the current connection status.",
      filename: "docs-07-connections.png",
      src: "/media/docs/connection.png",
      alt: "UGC Persona connections screenshot"
    }
  },
  {
    id: "billing",
    sidebarTitle: "Billing",
    sidebarDescription: "Understand credits, plans, and when to upgrade.",
    eyebrow: "Billing",
    title: "Billing: Credits, plans, and upgrades",
    description:
      "Credits and subscription logic follow the pricing structure already explained on the website. The docs should keep this section simple and point users back to the pricing page whenever they need plan details.",
    topics: [
      {
        title: "Credits are consumed by generation steps",
        body: "Image, copy, voice, and video generation all consume credits according to the current plan logic."
      },
      {
        title: "Upgrade when you need more volume",
        body: "If you need more credits, more production capacity, or a higher monthly volume, the correct path is to upgrade the account plan."
      },
      {
        title: "Use the pricing page as the source of truth",
        body: "For the latest billing details, plan structure, and upgrade options, the docs should send users back to the pricing section of the site."
      }
    ],
    resources: [
      {
        label: "Pricing page",
        href: "/#pricing"
      }
    ]
  }
];

function DocsLightbox({ image, onClose }) {
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 px-4 py-6 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <div className="relative w-full max-w-[96vw] md:max-w-[92vw]" onClick={(event) => event.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          className="absolute -right-2 -top-2 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-700 bg-slate-950 text-slate-100 shadow-[0_10px_30px_rgba(2,6,23,.55)] transition hover:border-cyan-300/60 hover:text-cyan-100"
          aria-label="Close image preview"
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5 fill-none stroke-current stroke-[2]" aria-hidden>
            <path d="M5 5l14 14M19 5L5 19" />
          </svg>
        </button>

        <div className="overflow-hidden rounded-[1.75rem] border border-slate-700 bg-slate-950 shadow-[0_30px_90px_rgba(2,6,23,.65)]">
          <img src={image.src} alt={image.alt} className="max-h-[88vh] w-full object-contain bg-slate-950" />
        </div>
        <div className="mt-3 space-y-1 text-sm text-slate-300">
          <span className="block font-semibold text-slate-100">{image.title}</span>
          <span className="block leading-6 text-slate-400">{image.caption}</span>
        </div>
      </div>
    </div>,
    document.body
  );
}

function DocsScreenshotCard({ code, title, caption, src, alt, pending = false }) {
  const [open, setOpen] = useState(false);

  if (pending) {
    return (
      <div className="relative w-full overflow-hidden rounded-[1.9rem] border border-dashed border-slate-700 bg-[radial-gradient(circle_at_top_left,rgba(99,102,241,.12),transparent_38%),rgba(2,6,23,.72)] p-4 shadow-[0_16px_34px_rgba(2,6,23,.38)] md:p-5">
        <div className="pointer-events-none absolute inset-0 opacity-60 [background-image:linear-gradient(rgba(148,163,184,.08)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,.08)_1px,transparent_1px)] [background-size:18px_18px]" />
        <div className="relative space-y-4">
          <div className="flex items-center justify-between gap-3">
            <span className="inline-flex rounded-full border border-slate-600 bg-slate-900 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300">
              {code}
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Pending screenshot</span>
          </div>
          <div className="rounded-[1.5rem] border border-slate-700 bg-slate-950/85 p-5 md:p-6">
            <div className="flex items-center gap-1.5 text-slate-600">
              <span className="h-2.5 w-2.5 rounded-full bg-rose-400/70" />
              <span className="h-2.5 w-2.5 rounded-full bg-amber-300/70" />
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
            </div>
            <div className="mt-5 rounded-[1.4rem] border border-dashed border-slate-700 bg-slate-900/70 p-6 text-center">
              <p className="text-sm font-semibold text-slate-100">{title}</p>
              <p className="mt-2 text-sm leading-6 text-slate-400">{caption}</p>
            </div>
          </div>
          <p className="text-base font-semibold text-slate-100">{title}</p>
          <p className="text-sm leading-6 text-slate-400">{caption}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group relative w-full overflow-hidden rounded-[1.9rem] border border-slate-700 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,.1),transparent_35%),rgba(2,6,23,.8)] p-4 text-left shadow-[0_16px_34px_rgba(2,6,23,.42)] transition hover:border-cyan-400/40 hover:shadow-[0_20px_48px_rgba(2,6,23,.5)] md:p-5"
      >
        <div className="pointer-events-none absolute inset-0 opacity-70 [background-image:linear-gradient(rgba(148,163,184,.06)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,.06)_1px,transparent_1px)] [background-size:18px_18px]" />
        <div className="relative space-y-4">
          <div className="flex items-center justify-between gap-3">
            <span className="inline-flex rounded-full border border-cyan-400/35 bg-cyan-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-100">
              {code}
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400 transition group-hover:text-cyan-200">
              Click to expand
            </span>
          </div>

          <div className="overflow-hidden rounded-[1.5rem] border border-slate-700 bg-slate-950/90">
            <img src={src} alt={alt} className="block h-auto w-full" loading="lazy" decoding="async" />
          </div>

          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-base font-semibold text-slate-100">{title}</p>
              <p className="mt-1 text-sm leading-6 text-slate-400">{caption}</p>
            </div>
            <span className="inline-flex shrink-0 items-center gap-2 rounded-full border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs font-semibold text-slate-200">
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-[1.8]" aria-hidden>
                <path d="M8 3H3v5" />
                <path d="M3 3l7 7" />
                <path d="M16 3h5v5" />
                <path d="M21 3l-7 7" />
                <path d="M8 21H3v-5" />
                <path d="M3 21l7-7" />
                <path d="M16 21h5v-5" />
                <path d="M21 21l-7-7" />
              </svg>
              Expand
            </span>
          </div>
        </div>
      </button>

      {open && src ? <DocsLightbox image={{ src, alt, title, caption }} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function SidebarGlyph() {
  return (
    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-cyan-400/25 bg-cyan-500/10 text-cyan-100 shadow-[0_0_0_1px_rgba(34,211,238,.05)]">
      <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-[1.8]" aria-hidden>
        <path d="M7 6h7l3 3v9H7z" />
        <path d="M14 6v3h3" />
        <path d="M9 13h6" />
        <path d="M9 16h4" />
      </svg>
    </span>
  );
}

export default function DocsIndexPage({ auth }) {
  const screenshotEntries = DOCS_CHAPTERS.filter((chapter) => chapter.screenshot).map((chapter) => chapter.screenshot);

  return (
    <LegalLayout
      auth={auth}
      eyebrow="Docs"
      title="Generation Wizard Guide"
      intro="UGC Persona helps brands create realistic AI people who can tell a product story through image, text, voice, and final video. This guide explains the wizard step by step, from the first image decision to publishing, plus the connection and billing logic around the workflow."
    >
      <div className="grid gap-6 md:grid-cols-12 md:items-start">
        <aside className="md:col-span-4 md:sticky md:top-24 md:self-start lg:col-span-3">
          <div className="rounded-2xl border border-slate-800 bg-slate-950 p-5 shadow-[0_14px_40px_rgba(2,6,23,.36)] md:max-h-[calc(100vh-7rem)] md:overflow-y-auto">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-200">Chapters</p>
            <p className="mt-2 text-sm leading-6 text-slate-400">
              Start with the flow overview, then follow the wizard in order from routing to publishing.
            </p>

            <nav className="mt-5 space-y-1.5 md:pr-1">
              {DOCS_CHAPTERS.map((chapter) => (
                <a
                  key={chapter.id}
                  href={`#${chapter.id}`}
                  className="flex items-start gap-3 rounded-xl border border-transparent px-3 py-3 text-left transition hover:border-slate-700 hover:bg-slate-900/80"
                >
                  <SidebarGlyph />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold leading-5 text-slate-100">{chapter.sidebarTitle || chapter.title}</span>
                    <span className="mt-1 block text-xs leading-5 text-slate-500">{chapter.sidebarDescription || chapter.description}</span>
                  </span>
                </a>
              ))}
              <a
                href="#screenshot-pack"
                className="flex items-start gap-3 rounded-xl border border-transparent px-3 py-3 text-left transition hover:border-slate-700 hover:bg-slate-900/80"
              >
                <SidebarGlyph />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-slate-100">Assets</span>
                  <span className="mt-1 block text-xs leading-5 text-slate-500">Screenshot filename checklist and placeholders.</span>
                </span>
              </a>
            </nav>
          </div>
        </aside>

        <div className="space-y-5 md:col-span-8 lg:col-span-9">
          {DOCS_CHAPTERS.map((chapter) => (
            <section
              key={chapter.id}
              id={chapter.id}
              className="scroll-mt-28 rounded-2xl border border-slate-800 bg-slate-950/95 p-5 shadow-[0_14px_40px_rgba(2,6,23,.36)] md:p-6"
            >
              <div className="space-y-5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-violet-300">{chapter.eyebrow}</p>
                  <h2 className="mt-2 text-2xl font-semibold tracking-tight text-white md:text-3xl">{chapter.title}</h2>
                  <p className="mt-3 text-sm leading-7 text-slate-300 md:text-[15px]">{chapter.description}</p>

                  {chapter.flow ? (
                    <div className="mt-4 rounded-2xl border border-cyan-400/20 bg-cyan-500/10 px-4 py-3 text-sm leading-6 text-cyan-50">
                      {chapter.flow}
                    </div>
                  ) : null}

                  <div className="mt-5 grid gap-3 md:grid-cols-2">
                    {chapter.topics.map((topic) => (
                      <article key={topic.title} className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
                        <p className="text-sm font-semibold text-slate-100">{topic.title}</p>
                        <p className="mt-2 text-sm leading-6 text-slate-400">{topic.body}</p>
                      </article>
                    ))}
                  </div>

                  {chapter.resources?.length ? (
                    <div className="mt-5 rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
                      <p className="text-sm font-semibold text-slate-100">Recommended references</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {chapter.resources.map((resource) => {
                          const isExternal = /^https?:\/\//.test(resource.href);
                          return (
                            <a
                              key={resource.href}
                              href={resource.href}
                              target={isExternal ? "_blank" : undefined}
                              rel={isExternal ? "noreferrer" : undefined}
                              className="inline-flex items-center rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1.5 text-sm font-medium text-cyan-100 transition hover:border-cyan-300/50 hover:bg-cyan-500/15"
                            >
                              {resource.label}
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>

                {chapter.screenshot ? <DocsScreenshotCard {...chapter.screenshot} /> : null}
              </div>
            </section>
          ))}

          <section
            id="screenshot-pack"
            className="scroll-mt-28 rounded-2xl border border-slate-800 bg-slate-950/95 p-5 shadow-[0_14px_40px_rgba(2,6,23,.36)] md:p-6"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-200">Assets</p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-white md:text-3xl">Screenshot filename checklist</h2>
            <p className="mt-3 text-sm leading-7 text-slate-300 md:text-[15px]">
              When the real screenshots are captured, keep the active step title and the result state visible in the same frame. The placeholders above
              already reserve the filenames below one-to-one.
            </p>

            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {screenshotEntries.map((entry) => (
                <div key={entry.filename} className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4">
                  <p className="font-mono text-sm text-cyan-200">{entry.filename}</p>
                  <p className="mt-2 text-sm font-semibold text-slate-100">{entry.title}</p>
                  <p className="mt-1 text-sm leading-6 text-slate-400">{entry.caption}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </LegalLayout>
  );
}
